<template>
	<div class="results-toolbar results-toolbar--variant-three">
		<div class="results-toolbar__search">
			<Search
				:placeholder="placeholder"
				v-model.lazy="filters.search"
				:timeout="300"
				:width="searchWidth"
				@change="emitValues"
			/>
		</div>
		<Button
			v-if="showResetFilters"
			class="results-toolbar--customer-forms__reset-filters"
			className="btn--link"
			labelText="Restore Filters"
			:btnClick="resetFilters"
		/>
		<div class="results-toolbar--customer-forms__desktop" ref="desktopRef">
			<DatePicker
				v-model="parseDate"
				@change="emitValues"
				:width="datePickerWidth"
			/>
			<DropdownV2
				v-model="filters.changedByUserId"
				:options="userOptions"
				placeholder="All Users"
				:dropdownWidth="ddUserWidth"
				@change="emitValues"
			/>
		</div>
		<div class="results-toolbar--customer-forms__mobile" ref="mobileRef">
			<Button
				className="btn--primary"
				iconClassName="icon-filter"
				:labelText="filterLabel"
				:iconBeforeLabel="true"
				:btnClick="() => (displayFiltersModal = true)"
			/>
		</div>
		<Modal
			:display="displayFiltersModal"
			title="Filters"
			@show="displayFiltersModal = false"
		>
			<template #modal-content>
				<div class="results-toolbar--modal-filters">
					<DatePicker v-model="parseDate" :width="300" />
					<DropdownV2
						v-model="filters.changedByUserId"
						:options="userOptions"
						:dropdownWidth="'300px'"
						placeholder="All Users"
					/>
				</div>
			</template>
			<template #modal-footer>
				<ButtonGroup>
					<template #container>
						<Button
							labelText="Apply"
							className="btn--primary"
							:btnClick="applyFilterUsingModal"
							:disabled="!filtersModalHaveChanges"
						/>
						<Button
							labelText="Cancel"
							className="btn--link"
							:btnClick="closeModalFilters"
						/>
					</template>
				</ButtonGroup>
			</template>
		</Modal>
	</div>
</template>

<script>
import Search from '@/components/forms/search/search';
import DatePicker from '@/components/forms/date-picker/date-picker';
import ButtonGroup from '@/components/forms/button/button-group';
import Button from '@/components/forms/button/button';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import Modal from '@/components/shells/modal/modals';

// libraries
import { formatDate } from '@/utils/datetime';
import { deepEqualNoFn } from '@/utils/object';
import {  parse } from 'date-fns';
import { countsDiffObjects } from '@/utils/function';
import _ from 'lodash';

export default {
	name: 'HistoryFilters',
	components: {
		Search,
		DatePicker,
		ButtonGroup,
		Button,
		DropdownV2,
		Modal
	},
	props: {
		value: {
			type: Object,
			default: () => {}
		},
		defaultFilters: Object,
		counter: String,
		userOptions: Array,
		placeholder: {
			type: String,
			default: 'Search by name'
		},
		searchWidth: {
			type: Number,
			default: 220
		},
		datePickerWidth: {
			type: [String, Number],
			default: null
		},
		ddUserWidth: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			displayFiltersModal: false,
			filters: {},
			currentDefaultFilters: {
				...this.value
			},
			keysToPick: ['minDatetime', 'maxDatetime', 'changedByUserId']
		};
	},
	created() {
		this.updateLocalValues(this.value);
	},
	computed: {
		filtersModalHaveChanges() {
			return !deepEqualNoFn(this.filters, this.value, true);
		},
		showResetFilters() {
			return !deepEqualNoFn(
				this.filteredFilters,
				this.filteredCurrentDefaultFilters,
				true
			);
		},
		filteredFilters() {
			return _.pick(this.filters, this.keysToPick);
		},
		filteredCurrentDefaultFilters() {
			return _.pick(this.defaultFilters, this.keysToPick);
		},
		totalFiltersNumber() {
			return countsDiffObjects(
				this.filteredCurrentDefaultFilters,
				this.filteredFilters
			);
		},
		filterLabel() {
			if (this.totalFiltersNumber) {
				return ` Filters (${this.totalFiltersNumber})`;
			}
			return ' Filters';
		},
		parseDate: {
			get() {
				if (this.filters.minDatetime) {
					return {
						startDate: parse(
							this.filters.minDatetime,
							'MM-dd-yyyy',
							new Date()
						),
						endDate: parse(
							this.filters.maxDatetime,
							'MM-dd-yyyy',
							new Date()
						)
					};
				} else {
					return {
						startDate: parse(
							this.defaultFilters.minDatetime,
							'MM-dd-yyyy',
							new Date()
						),
						endDate: parse(
							this.defaultFilters.maxDatetime,
							'MM-dd-yyyy',
							new Date()
						)
					};
				}
			},
			set(value) {
				if (value.startDate) {
					this.filters.minDatetime = formatDate(value.startDate);
					this.filters.maxDatetime = formatDate(value.endDate);
				} else {
					this.filters.minDatetime = this.defaultFilters.minDatetime;
					this.filters.maxDatetime = this.defaultFilters.maxDatetime;
				}
			}
		}
	},

	methods: {
		updateLocalValues(obj) {
			this.filters = _.cloneDeep(obj);
		},
		applyFilterUsingModal() {
			this.displayFiltersModal = false;
			this.$emit('input', { ...this.filters });
		},
		resetFilters() {
			const resetFilters = Object.assign(
				{},
				this.filters,
				this.filteredCurrentDefaultFilters
			);
			this.updateLocalValues(resetFilters);
			this.currentDefaultFilters = _.cloneDeep(this.filters);
			this.$emit('input', { ...this.filters });
		},
		closeModalFilters() {
			this.filters = _.cloneDeep(this.value);
			this.displayFiltersModal = false;
		},
		emitValues() {
			this.$emit('input', { ...this.filters });
		}
	},
	watch: {
		value(newValue) {
			this.updateLocalValues(newValue);
		}
	}
};
</script>

<style lang="scss" scoped>
::v-deep .modal__body {
	display: flex;
	gap: $gutter-md;
}

.results-toolbar--modal-filters {
	display: grid;
	gap: $gutter-lg;
}

.results-toolbar--customer-forms {
	&__reset-filters {
		margin-left: auto;
	}

	&__mobile {
		margin-left: $gutter-lg;

		@include mq($from: md) {
			margin-left: 0;
		}

		@include mq($from: md-l) {
			display: none;
		}
	}

	&__desktop {
		display: none;
		z-index: 100;

		@include mq($from: md-l) {
			display: flex;
			gap: $gutter-lg;
		}
	}
}
</style>
